import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash, Plus, Package, BarChart3, Settings, Upload } from 'lucide-react';

export default function SellerDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({ views: 0, likes: 0, sales: 0, product_count: 0 });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // New Product Form
  const [newProduct, setNewProduct] = useState({
    title: '', description: '', price: '', category: 'Jewelry', 
    image_url: '', delivery_option: 'included', delivery_price: 0
  });

  useEffect(() => {
    if (user && user.role === 'seller') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, productsRes, ordersRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/stats`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products?seller_id=${user.id}`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/seller/orders`, { headers })
      ]);

      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error("Fetch dashboard error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}` 
        }
      });
      setNewProduct(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/products`, 
        { ...newProduct, price: parseFloat(newProduct.price), delivery_price: parseFloat(newProduct.delivery_price) }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Product created!");
      setNewProduct({ title: '', description: '', price: '', category: 'Jewelry', image_url: '', delivery_option: 'included', delivery_price: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create product");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{t('sellerDashboard')}</h1>
        <div className="text-sm text-stone-500">Welcome, {user?.name}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
        <StatCard title="Total Sales" value={stats.sales} icon={<Package className="text-primary" />} />
        <StatCard title="Total Views" value={stats.views} icon={<BarChart3 className="text-blue-500" />} />
        <StatCard title="Products" value={stats.product_count} icon={<Settings className="text-green-500" />} />
        <StatCard title="Store Rating" value="4.8" icon={<span className="text-yellow-500">★</span>} />
      </div>

      <Tabs defaultValue="products" className="space-y-6">
        <TabsList className="bg-stone-100 p-1 rounded-lg">
          <TabsTrigger value="products">My Products</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="add">Add Product</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="p-4 font-medium text-stone-600">Product</th>
                  <th className="p-4 font-medium text-stone-600">Price</th>
                  <th className="p-4 font-medium text-stone-600">Views</th>
                  <th className="p-4 font-medium text-stone-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <td className="p-4 font-medium flex items-center gap-3">
                      <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover bg-stone-100" />
                      {p.title}
                    </td>
                    <td className="p-4">{p.price} DZD</td>
                    <td className="p-4">{p.views}</td>
                    <td className="p-4">
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteProduct(p.id)}>
                        <Trash size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && <div className="p-8 text-center text-stone-500">No products yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h3 className="font-bold mb-4">Recent Orders</h3>
            {orders.length === 0 ? (
              <p className="text-stone-500">No orders yet.</p>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="border p-4 rounded-lg flex justify-between items-center">
                    <div>
                      <div className="font-bold">Order #{order.id.slice(0, 8)}</div>
                      <div className="text-sm text-stone-500">{order.buyer_name} - {new Date(order.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-primary">{order.total} DZD</div>
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">{order.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="add">
          <div className="max-w-2xl bg-white p-8 rounded-xl border border-stone-200">
            <h2 className="text-xl font-bold mb-6">List New Product</h2>
            <form onSubmit={handleCreateProduct} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Product Title</Label>
                  <Input required value={newProduct.title} onChange={e => setNewProduct({...newProduct, title: e.target.value})} />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newProduct.category} onValueChange={v => setNewProduct({...newProduct, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Jewelry">Jewelry</SelectItem>
                      <SelectItem value="Pottery">Pottery</SelectItem>
                      <SelectItem value="Rugs">Rugs</SelectItem>
                      <SelectItem value="Clothing">Clothing</SelectItem>
                      <SelectItem value="Decor">Decor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea required value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} />
              </div>

              <div className="grid grid-cols-2 gap-6">
                 <div>
                  <Label>Price (DZD)</Label>
                  <Input type="number" required value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: e.target.value})} />
                </div>
                <div>
                  <Label>Product Image</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileUpload} 
                      className="cursor-pointer"
                    />
                  </div>
                  {uploading && <p className="text-xs text-blue-500 mt-1">Uploading...</p>}
                  {newProduct.image_url && !uploading && (
                    <div className="mt-2">
                      <p className="text-xs text-green-600 mb-1">Image uploaded!</p>
                      <img src={newProduct.image_url} alt="Preview" className="h-20 w-20 object-cover rounded border" />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Delivery Option</Label>
                  <Select value={newProduct.delivery_option} onValueChange={v => setNewProduct({...newProduct, delivery_option: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="included">Included in Price</SelectItem>
                      <SelectItem value="free">Free Delivery</SelectItem>
                      <SelectItem value="paid">Paid by Buyer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                 {newProduct.delivery_option === 'paid' && (
                  <div>
                    <Label>Delivery Cost (DZD)</Label>
                    <Input type="number" value={newProduct.delivery_price} onChange={e => setNewProduct({...newProduct, delivery_price: e.target.value})} />
                  </div>
                )}
              </div>

              <Button type="submit" disabled={uploading} className="w-full btn-primary">
                {uploading ? "Uploading Image..." : "Publish Product"}
              </Button>
            </form>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ title, value, icon }) => (
  <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm flex items-center justify-between">
    <div>
      <p className="text-stone-500 text-sm font-medium">{title}</p>
      <p className="text-2xl font-bold text-stone-900 mt-1">{value}</p>
    </div>
    <div className="bg-stone-50 p-3 rounded-full">{icon}</div>
  </div>
);
